import type { FxRateDocument } from '@interfaces/portfolioReport';
import { CurrencyCodes } from '@onevesthq/ov-enums';
import { MultiCurrencyIconWithColor } from '@ovComponents/1-primative/multiCurrencyIconWithColor/multiCurrencyIconWithColor';
import { TableCell } from '@ovComponents/2-component/table/table';
import { CurrencyFlag } from '@ovComponents/3-pattern/currencyFlag/currencyFlag';
import { CurrencyBreakdownTooltip } from '@ovComponents/3-pattern/currencyIndicator/currencyIndicator';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Box, Tooltip, Typography, useTheme } from 'hephaestus/web';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TESTID_PORTFOLIO_WIDGET_HOLDINGS_TABLE_ROW_CELL } from '../portfolioTestIds';
import ChangeTooltip from './changeTooltip';
import type { DraftSubTradeRequest, PortfolioSubAccount } from './interfaces';
import {
  getPortfolioMemberColumnSx,
  getPortfolioPairedMemberColumnSx,
  getSubAccountCurrentCents,
  getSubAccountOriginalCurrentCents,
  PORTFOLIO_CELL_HEIGHT,
  sumPendingTradesCentsByCondition,
} from './utils';

const TooltipTitle = ({
  subAccount,
  fxRate,
}: {
  subAccount: PortfolioSubAccount;
  fxRate: number;
}) => {
  const { t } = useTranslation(['components', 'accountTypes']);

  const pendingWithdrawCents = subAccount?.pendingWithdrawCents
    ? subAccount.pendingWithdrawCents * fxRate
    : null;
  const pendingContributionCents =
    subAccount?.pendingContributionCents &&
    subAccount?.pendingContributionCents < 0
      ? Math.abs(subAccount.pendingContributionCents) * fxRate
      : null;

  return (
    <div style={{ whiteSpace: 'pre-line' }}>
      {t('portfolioTable.pendingWithdraw', {
        amount: formatMoneyValue(
          pendingWithdrawCents ?? pendingContributionCents ?? 0,
        ),
      })}
    </div>
  );
};

export const SubAccountTableCell = ({
  subAccount,
  draftTrades,
  pendingTrades,
  index = 0,
  currency = CurrencyCodes.CAD,
  isMultiCurrencyPortfolio = false,
  fxRate = 1,
  fxRates = [],
  pairedWithCustomTarget = false,
  highlightPendingWithdraw = true,
  cashDisplayProposedEnd = false,
}: {
  subAccount: PortfolioSubAccount;
  draftTrades: Map<string, DraftSubTradeRequest>;
  pendingTrades: Map<string, DraftSubTradeRequest>;
  index?: number;
  currency?: CurrencyCodes;
  isMultiCurrencyPortfolio?: boolean;
  fxRate?: number;
  fxRates?: FxRateDocument[];
  pairedWithCustomTarget?: boolean;
  highlightPendingWithdraw?: boolean;
  cashDisplayProposedEnd?: boolean;
}) => {
  const { t } = useTranslation(['components', 'accountTypes']);
  const theme = useTheme();

  const pendingTradesCents = useMemo(
    () =>
      sumPendingTradesCentsByCondition({
        maps: [draftTrades, pendingTrades],
        callbackFn: (trade) => trade.subAccountId === subAccount.id,
        currency,
        fxRates,
      }),
    [subAccount, draftTrades, pendingTrades, currency, fxRates],
  );

  const cashTargetCents =
    subAccount.expectedCashCents * fxRate -
    subAccount.cashOnHoldToTradeCents * fxRate;

  const current = useMemo(
    () =>
      cashDisplayProposedEnd
        ? cashTargetCents
        : getSubAccountCurrentCents({
            subAccount,
            subAccountPendingTradesCents: pendingTradesCents,
            fxRate,
          }),
    [
      cashDisplayProposedEnd,
      cashTargetCents,
      subAccount,
      pendingTradesCents,
      fxRate,
    ],
  );

  const originalValuesCurrent = useMemo(
    () =>
      getSubAccountOriginalCurrentCents(subAccount, [
        draftTrades,
        pendingTrades,
      ]),
    [subAccount, draftTrades, pendingTrades],
  );

  const isAnyOfOriginalValuesNegative = useMemo(
    () =>
      originalValuesCurrent?.some((originalValue) => originalValue.current < 0),
    [originalValuesCurrent],
  );

  const originalValuesCount = subAccount?.originalValues?.length ?? 0;

  const isSubAccountPendingWithdrawal =
    highlightPendingWithdraw &&
    (subAccount?.pendingWithdrawCents > 0 ||
      subAccount?.pendingContributionCents < 0);

  return (
    <TableCell
      sx={{
        ...(pairedWithCustomTarget
          ? getPortfolioPairedMemberColumnSx(subAccount.name)
          : getPortfolioMemberColumnSx(subAccount.name)),
        height: PORTFOLIO_CELL_HEIGHT.cash,
      }}
      data-testid={TESTID_PORTFOLIO_WIDGET_HOLDINGS_TABLE_ROW_CELL(0, index)}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'start',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box>
          <Tooltip
            title={
              isSubAccountPendingWithdrawal ? (
                <TooltipTitle subAccount={subAccount} fxRate={fxRate} />
              ) : null
            }
          >
            <Typography
              variant='bodySmall'
              component='p'
              sx={{
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
                color: isSubAccountPendingWithdrawal
                  ? theme.color.warning
                  : undefined,
              }}
            >
              {t('portfolioTable.target')}:{' '}
              <b>{formatMoneyValue(cashTargetCents)}</b>
            </Typography>
          </Tooltip>

          <Typography
            variant='bodySmall'
            component='p'
            sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
          >
            {t('portfolioTable.current')}: <b>{formatMoneyValue(current)}</b>
          </Typography>
        </Box>

        {isMultiCurrencyPortfolio && originalValuesCount > 0 && (
          <CurrencyBreakdownTooltip
            lines={(originalValuesCurrent ?? []).map((elem) => ({
              currency: elem.currency,
              marketValueCents: elem.current,
            }))}
          >
            <Box
              data-testid='multi-currency-container'
              sx={{ width: 20, height: 20, flexShrink: 0 }}
            >
              {originalValuesCount > 1 && (
                <MultiCurrencyIconWithColor
                  mainColor={
                    isAnyOfOriginalValuesNegative
                      ? theme.color.negative
                      : undefined
                  }
                  secondaryColor={
                    isAnyOfOriginalValuesNegative
                      ? theme.color.negative
                      : undefined
                  }
                />
              )}

              {originalValuesCount === 1 && (
                <CurrencyFlag
                  height={20}
                  width={20}
                  baseCurrency={
                    subAccount?.accountBaseCurrency ?? CurrencyCodes.CAD
                  }
                />
              )}
            </Box>
          </CurrencyBreakdownTooltip>
        )}

        <Box>
          {!cashDisplayProposedEnd && (
            <ChangeTooltip
              change={pendingTradesCents * -1}
              original={
                subAccount.cashCents * fxRate -
                subAccount.cashOnHoldToTradeCents * fxRate
              }
              flip
              total
            />
          )}
        </Box>
      </Box>
    </TableCell>
  );
};
